import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, Input, Row, Col, message, Switch, Card, Alert, Divider, Select } from 'antd';
import { DollarOutlined, SaveOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import { motion } from "framer-motion";
import apiClient from 'services/apiClient';
import { useMyContext } from 'Context/MyContextProvider';

const { Option } = Select;

const InstaMojoGateway = ({ api, authToken, navigate }) => {
    const { Permisson } = useMyContext();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [isEnabled, setIsEnabled] = useState(false);
    const [apiKey, setApiKey] = useState('');
    const [authToken_, setAuthToken_] = useState('');
    const [environment, setEnvironment] = useState('sandbox');
    const [salt, setSalt] = useState('');


    const fetchGatewaySettings = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiClient.get(`${api}payment-gateway/instamojo`, {
                headers: {
                    'Authorization': 'Bearer ' + authToken,
                }
            });
            const data = response.data;
            
            // Handle the response according to your table structure with credentials object
            setIsEnabled(data.isEnabled || data.status === 'active' || false);
            
            // Extract from credentials object
            const credentials = data.credentials || {};
            setApiKey(credentials.apiKey || '');
            setAuthToken_(credentials.authToken || '');
            setSalt(credentials.salt || '');
            setEnvironment(data.environment || 'sandbox');

            form.setFieldsValue({
                api_key: credentials.apiKey || '',
                auth_token: credentials.authToken || '',
                salt: credentials.salt || '',
                environment: data.environment || 'sandbox',
            });
        } catch (error) {
            console.error('Error fetching InstaMojo settings:', error);
            // If no settings found, set default values
            form.setFieldsValue({
                environment: 'sandbox'
            });
        } finally {
            setLoading(false);
        }
    }, [api, authToken, form]);

    useEffect(() => {
        fetchGatewaySettings();
    }, [fetchGatewaySettings]);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const payload = {
                gateway: 'instamojo',
                credentials: {
                    apiKey: values.api_key,
                    authToken: values.auth_token,
                    salt: values.salt
                },
                environment: values.environment, // 'sandbox' or 'production'
                isEnabled: isEnabled,
                status: isEnabled ? 'active' : 'inactive'
            };

            const response = await apiClient.post(`${api}payment-gateway/instamojo`, payload, {
                headers: {
                    'Authorization': 'Bearer ' + authToken,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                message.success('InstaMojo settings updated successfully!');
                // Refresh the data after successful update
                fetchGatewaySettings();
            } else {
                message.error(response.data.message || 'Failed to update InstaMojo settings');
            }
        } catch (error) {
            console.error('Error updating InstaMojo settings:', error);
            message.error('Error updating InstaMojo settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSwitchChange = (checked) => {
        setIsEnabled(checked);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <Card bordered={false} className='shadow-none'>
                <div className="d-flex align-items-center mb-4">
                    <div>
                        <h2 className="mb-0">InstaMojo Gateway</h2>
                        <p className="text-muted mb-0">Configure your InstaMojo payment gateway settings</p>
                    </div>
                </div>

                <Alert
                    message="Important"
                    description="InstaMojo is a popular payment gateway in India. Make sure to test your integration in sandbox mode before going live."
                    type="info"
                    showIcon
                    className="mb-4"
                />

                <Row gutter={ROW_GUTTER}>
                    <Col xs={24} sm={24} md={17}>
                        <Card className="border border-light rounded-3">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="mb-0">Gateway Status</h5>
                                <Switch
                                    checked={isEnabled}
                                    onChange={handleSwitchChange}
                                    checkedChildren="Enabled"
                                    unCheckedChildren="Disabled"
                                />
                            </div>

                            <Divider />

                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={onFinish}
                                className="mt-4"
                            >
                                <Row gutter={ROW_GUTTER}>
                                    <Col xs={24} sm={24} md={12}>
                                        <Form.Item
                                            label="Environment"
                                            name="environment"
                                            rules={[
                                                { required: true, message: 'Please select environment' }
                                            ]}
                                        >
                                            <Select
                                                placeholder="Select environment"
                                                value={environment}
                                                onChange={(value) => setEnvironment(value)}
                                                className="rounded-2"
                                            >
                                                <Option value="sandbox">Sandbox (Test)</Option>
                                                <Option value="production">Production (Live)</Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={ROW_GUTTER}>
                                    <Col xs={24} sm={24} md={12}>
                                        <Form.Item
                                            label="API Key"
                                            name="api_key"
                                            rules={[
                                                { required: true, message: 'Please enter your InstaMojo API Key' }
                                            ]}
                                        >
                                            <Input
                                                placeholder="Enter API Key"
                                                value={apiKey}
                                                onChange={(e) => setApiKey(e.target.value)}
                                                className="rounded-2"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={24} md={12}>
                                        <Form.Item
                                            label="Auth Token"
                                            name="auth_token"
                                            rules={[
                                                { required: true, message: 'Please enter your InstaMojo Auth Token' }
                                            ]}
                                        >
                                            <Input.Password
                                                placeholder="Enter Auth Token"
                                                value={authToken_}
                                                onChange={(e) => setAuthToken_(e.target.value)}
                                                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                                                className="rounded-2"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={ROW_GUTTER}>
                                    <Col xs={24} sm={24} md={12}>
                                        <Form.Item
                                            label="Salt (Optional)"
                                            name="salt"
                                        >
                                            <Input
                                                placeholder="Enter Salt"
                                                value={salt}
                                                onChange={(e) => setSalt(e.target.value)}
                                                className="rounded-2"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item className="mt-2">
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={loading}
                                        className='float-right'
                                        icon={<SaveOutlined />}
                                    >
                                       Save InstaMojo Settings
                                    </Button>
                                </Form.Item>
                            </Form>
                        </Card>
                    </Col>

                    <Col xs={24} sm={24} md={7}>
                        <Card className="border border-light rounded-3 bg-light">
                            <h6 className="fw-bold mb-3">Setup Instructions</h6>
                            <div className="small text-muted">
                                <ol className="ps-3">
                                    <li className="mb-2">
                                        Log in to your <a href="https://www.instamojo.com" target="_blank" rel="noopener noreferrer">InstaMojo Dashboard</a>
                                    </li>
                                    <li className="mb-2">
                                        Navigate to API & Plugins section
                                    </li>
                                    <li className="mb-2">
                                        Generate or copy your API Key and Auth Token
                                    </li>
                                    <li className="mb-2">
                                        Configure webhook URLs for payment notifications
                                    </li>
                                    <li className="mb-2">
                                        Test payments in sandbox mode first
                                    </li>
                                    <li className="mb-0">
                                        Switch to production when ready to go live
                                    </li>
                                </ol>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Card>
        </motion.div>
    );
};

export default InstaMojoGateway;
